import * as turf from '@turf/turf';

/**
 * Returns the simplification tolerance based on the current map zoom level.
 * Lower zoom = more aggressive simplification (fewer points needed).
 * Higher zoom = less simplification (more detail visible).
 */
function getToleranceForZoom(zoom: number): number {
	if (zoom <= 6) return 0.05;
	if (zoom <= 9) return 0.01;
	if (zoom <= 12) return 0.005;
	if (zoom <= 14) return 0.002;
	return 0.0005;
}

const MIN_PATH_LENGTH_FOR_SIMPLIFICATION = 100;

/**
 * Simplifies a polygon path using the Ramer-Douglas-Peucker algorithm (via Turf.js)
 * based on the current map zoom level.
 *
 * - Editable polygons are never simplified (user needs full vertex precision).
 * - Paths with fewer than 100 points are returned as-is (overhead not worth it).
 * - Uses [lng, lat] internally for Turf/GeoJSON, regardless of latLngFF setting,
 *   since input is already google.maps.LatLng[].
 */
export function simplifyPolygonPath(
	path: google.maps.LatLng[],
	zoom: number,
	isEditable: boolean
): google.maps.LatLng[] {
	if (isEditable || !path || path.length < MIN_PATH_LENGTH_FOR_SIMPLIFICATION) {
		return path;
	}

	const tolerance = getToleranceForZoom(zoom);

	// Convert LatLng[] to GeoJSON coordinate ring [lng, lat][]
	// GeoJSON polygons require the ring to be closed (first === last)
	const coordinates: [number, number][] = path.map(p => [p.lng(), p.lat()]);
	const firstCoord = coordinates[0];
	const lastCoord = coordinates[coordinates.length - 1];

	if (firstCoord[0] !== lastCoord[0] || firstCoord[1] !== lastCoord[1]) {
		coordinates.push([...firstCoord] as [number, number]);
	}

	try {
		const geojsonPolygon = turf.polygon([coordinates]);
		const simplified = turf.simplify(geojsonPolygon, {
			tolerance,
			highQuality: false,
			mutate: false
		});

		const simplifiedCoords = simplified.geometry.coordinates[0];

		// Convert back to google.maps.LatLng[], removing the closing duplicate
		return simplifiedCoords.slice(0, -1).map(coord => new google.maps.LatLng(coord[1], coord[0]));
	} catch {
		// If simplification fails for any reason, return the original path
		return path;
	}
}
